import { defineStore } from 'pinia';
import _ from 'lodash';
import { axios } from '@/utils/axios';
import { getApiPathPrefix, getExtraData, convertBrTag } from '@/utils/utils';
import { merge, extend } from 'lodash';
import {
  BannerCouponState,
  ICallFindCoupon,
  ICallFindCouponResponse,
  ICallCertifyNumber,
} from 'src/components/modules/bannerCoupon/BannerCoupon';
import {
  formatCouponList,
  maskingUserInfo,
  formattingInternationalNumber,
  NOT_EXISTS_EMAIL,
  NOT_EXISTS_PHONENUMBER,
  DISABLE_CERTIFICATION_BY_EMAIL_LIMIT,
  EXPIRED_COUNT_TARGET_CERTIFICATION,
  INVALID_CODE_TARGET_CERTIFICATION,
  DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT,
  DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT,
  DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT,
  EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL,
  BLOCKED_IP,
  isExistErrorCodeInSearch,
} from '@/utils/preorderUtil';
import { i18n, tHtml } from '@/utils/i18n';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { usePreorderCommonStore as preorderCommonStore } from '@/store/preorderCommon';
const { t } = i18n;
export const useBannerCouponStore = defineStore('bannerCouponStore', {
  state: (): BannerCouponState => ({
    bannerCouponStep: 1,
    layer1: {
      title: '',
      date: {
        useStartTime: true,
        useEndTime: true,
        date: '쿠폰등록 기간 : YYYY-MM-DD (hh:mm) ~ YYYY-MM-DD (hh:mm)',
        useDefaultText: true,
        utcString: '',
      },
    },
    layer2: {
      title: '',
      notice: {
        useStartTime: true,
        useEndTime: true,
        registerText:
          '쿠폰등록 기간 : YYYY-MM-DD (hh:mm) ~ YYYY-MM-DD (hh:mm) <br />※ 쿠폰은 계정당 1회만 등록할 수 있습니다.',
        useDefaultText: true,
        utcString: '',
      },
    },
    bannerPhoneOrEmailInput: null,
    bannerCertifyInput: null,
    useCertification: true, // 쿠폰 확인에서는 항상 true
    isSendedCertifyNum: false, // 인증callCertifyNumberPostData 번호 전송 완료 됬는지
    isInResendProcess: false,
    userInputedData: {
      inputedCountryNumber: '82',
      inputedPhone: '',
      inputedEmail: '',
      selectedDropdownPrePhone: '',
      inputedCertifyNum: '',
    },
    userData: '',
    couponList: [],
  }),

  getters: {
    // 전화번호 또는 이메일 입력이 비어 있는가?
    isEmptyInputedValue(state: BannerCouponState) {
      if (preorderCommonStore().getUsePhone) {
        return !state.userInputedData.inputedPhone;
      } else {
        return !state.userInputedData.inputedEmail;
      }
    },

    // 최종 결정된 국가 번호 : 스테틱한 국가 번호 또는 드롭다운으로 선택한 국가 번호
    getInputedCountry(state: BannerCouponState) {
      if (preorderCommonStore().getUsingAreaCode || !preorderCommonStore().getUsingDropDown)
        return preorderCommonStore().getSpecificCountryNumber;
      else
        return (
          state.userInputedData.selectedDropdownPrePhone ||
          preorderCommonStore().getDropDownOptions[0].value
        );
    },

    // 최종 결정된 전화번호 : 지역번호 있는경우 지역번호 포함
    getInputedPhone(state: BannerCouponState) {
      if (preorderCommonStore().getUsingAreaCode)
        return state.userInputedData.selectedDropdownPrePhone + state.userInputedData.inputedPhone;
      else return state.userInputedData.inputedPhone;
    },

    // Step2 로 가도 되는가?
    getIsReadyNextStep(state: BannerCouponState) {
      // [인증타입] 인증 번호 받았으며 인증 번호 입력 했는가?
      const isInputedCountyryNumber = state.useCertification
        ? !!state.userInputedData.inputedCertifyNum && !!state.isSendedCertifyNum
        : true;

      // 전화번호 또는 이메일 입력이 비어 있는가?
      const isInputedValue =
        !!state.userInputedData.inputedPhone || !!state.userInputedData.inputedEmail;

      // 애러는 없는가?
      return (
        isInputedCountyryNumber &&
        isInputedValue &&
        !preorderCommonStore().errorMessage.bannerCoupon.inputPhone &&
        !preorderCommonStore().errorMessage.bannerCoupon.inputEmail &&
        !preorderCommonStore().errorMessage.bannerCoupon.inputCertify
      );
    },

    getIsDisabledCertifyButton() {
      return (
        !!preorderCommonStore().errorMessage.bannerCoupon.inputPhone ||
        !!preorderCommonStore().errorMessage.bannerCoupon.inputEmail
      );
    },

    getIsReadyCallCertifyNum(state: BannerCouponState) {
      // 이메일 or 전화번호가 입력되어 있거나, 에러가 없을 때만 활성화
      // 인증번호가 전송되었고
      return !state.userInputedData.inputedEmail && !state.userInputedData.inputedPhone;
    },

    // 쿠폰 조회를 위한 인증번호 전송 API URL
    getApiUrlGettingCertification() {
      let result = '';
      if (preorderCommonStore().getUsePhone) {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createPhoneNumberCertificationForFind`;
      } else {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/createEmailCertificationForFind`;
      }
      return result;
    },

    // 쿠폰조회 API URL
    getApiUrlFind() {
      let result = '';
      if (preorderCommonStore().getUsePhone) {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/findByPhoneNumber`;
      } else {
        result = `${getApiPathPrefix()}/preorder/preOrderRecord/findByEmail`;
      }
      return result;
    },
  },

  actions: {
    setLayerData(data: any) {
      this.layer1 = extend({}, this.layer1, data.layer.step1);
      this.layer2 = extend({}, this.layer2, data.layer.step2);
    },
    setBannerCertifyInput(bannerCertify: HTMLInputElement | null) {
      this.bannerCertifyInput = bannerCertify;
    },
    setBannerPhoneOrEmailInput(bannerInput: HTMLInputElement | null) {
      this.bannerPhoneOrEmailInput = bannerInput;
    },

    setLoginUserPhoneOrEmail() {
      const loginUserInfo = preorderCommonStore().getLoginUserInfo;
      if (!loginUserInfo) return;
      const { phoneNumber = '', email = '' } = loginUserInfo;
      if (preorderCommonStore().getUseEmail && !this.userInputedData.inputedEmail) {
        this.userInputedData.inputedEmail = email;
      } else if (preorderCommonStore().getUsePhone && !this.userInputedData.inputedPhone) {
        if (phoneNumber && isPossiblePhoneNumber(phoneNumber)) {
          const { nationalNumber, areaCode, countryCode, withOutAreaCodeNumber } =
            formattingInternationalNumber(phoneNumber);
          if (preorderCommonStore().getUsingAreaCode) {
            this.userInputedData.selectedDropdownPrePhone = areaCode;
            this.userInputedData.inputedPhone = withOutAreaCodeNumber;
          } else {
            this.userInputedData.selectedDropdownPrePhone = countryCode;
            this.userInputedData.inputedPhone = nationalNumber;
          }
        } else this.userInputedData.inputedPhone = phoneNumber ? phoneNumber : '';
      }
    },

    // 인증 번호 발송
    // 전화번호 인증번호 받기
    // 이메일 인증 번호 받기
    async callCertifyNumber(data: ICallCertifyNumber) {
      try {
        await axios.post<string, boolean>(
          this.getApiUrlGettingCertification,
          new URLSearchParams(data).toString()
        );

        preorderCommonStore().resetErrorMessage();
        this.userInputedData.inputedCertifyNum = '';
        this.isInResendProcess = true; // 한번이라도 인증 번호 발송했다면 이후부터는 재발송 절차
        this.isSendedCertifyNum = true;
        preorderCommonStore().startTimer('bannerCoupon');
      } catch (error) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();
        preorderCommonStore().stopTimer(preorderCommonStore().getTimer);
        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else {
          if (isExistErrorCodeInSearch(errorCode)) {
            this.displayError(errorCode, errorMessage);
          } else if (preorderCommonStore().getUsePhone) {
            preorderCommonStore().errorMessage.bannerCoupon.inputPhone = errorMessage;
            this.bannerPhoneOrEmailInput?.focus();
          } else {
            preorderCommonStore().errorMessage.bannerCoupon.inputEmail = errorMessage;
            this.bannerPhoneOrEmailInput?.focus();
          }
        }
      }
    },

    // 전화번호 쿠폰 확인 (유저가 입력한 인증번호 전송)
    // 이메일 쿠폰 확인 (유저가 입력한 인증번호 전송)
    async callFindCoupon(data: ICallFindCoupon) {
      // call API
      try {
        const response = await axios.get<ICallFindCoupon, ICallFindCouponResponse>(
          this.getApiUrlFind,
          data
        );

        preorderCommonStore().resetErrorMessage();
        const { extraData = '', phoneNumber = '', email = '' } = response.data;
        this.userData = maskingUserInfo(
          phoneNumber,
          email,
          preorderCommonStore().getLocale === 'zh_TW'
        );
        this.couponList = formatCouponList(extraData);
        if (this.couponList === null) console.error('받은 쿠폰 정보가 없습니다.');
        this.bannerCouponStep = 2;
      } catch (error) {
        const errorMessage = axios.getErrorMessage();
        const errorCode = axios.getErrorCode();

        if (!axios.getIsAxiosError()) window.alert(errorMessage);
        else {
          if (isExistErrorCodeInSearch(errorCode)) {
            this.displayError(errorCode, errorMessage);
          } else {
            preorderCommonStore().errorMessage.bannerCoupon.inputCertify = errorMessage;
            this.bannerCertifyInput?.focus();
          }
        }
      }
    },

    displayError(errorCode: string, errorMessage: string) {
      const _showErrorAndFocus = (useFocus: boolean = true) => {
        if (!this.isSendedCertifyNum) {
          if (preorderCommonStore().getUsePhone) {
            preorderCommonStore().errorMessage.bannerCoupon.inputPhone = convertBrTag(errorMessage);
          } else {
            preorderCommonStore().errorMessage.bannerCoupon.inputEmail = convertBrTag(errorMessage);
          }
          if (useFocus) this.bannerPhoneOrEmailInput?.focus();
        } else {
          preorderCommonStore().errorMessage.bannerCoupon.inputCertify = convertBrTag(errorMessage);
          if (useFocus) this.bannerCertifyInput?.focus();
        }
      };
      /*
      NOT_EXISTS_EMAIL 등록된 기록이 없습니다.
      NOT_EXISTS_PHONENUMBER 사전예약 신청 내역이 없는 정보입니다.
      INVALID_CODE_TARGET_CERTIFICATION 인증번호가 올바르지 않습니다.

      DISABLE_CERTIFICATION_BY_EMAIL_LIMIT 동일 메일로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요
      EXPIRED_COUNT_TARGET_CERTIFICATION 동일 인증번호로 인증 실패가 계속되어 인증번호 발송이 차단되었습니다.
      EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL 동일 인증번호로 인증 실패가 계속되어 인증번호 발송이 차단되었습니다.
      DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT 동일 이메일 주소로 인증 시도가 계속되어 인증메일 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.
      DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT 동일 번호로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.
      DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT 동일 IP로 인증 시도가 계속되어 인증번호 발송이 차단되었습니다. 24시간 이후 다시 시도해 주세요.
      */
      if (
        errorCode === BLOCKED_IP ||
        errorCode === NOT_EXISTS_EMAIL ||
        errorCode === NOT_EXISTS_PHONENUMBER ||
        errorCode === INVALID_CODE_TARGET_CERTIFICATION
      ) {
        _showErrorAndFocus();
      } else if (
        errorCode === EXPIRED_COUNT_TARGET_CERTIFICATION ||
        errorCode === EXPIRED_COUNT_TARGET_CERTIFICATION_BY_EMAIL ||
        errorCode === DISABLE_CERTIFICATION_BY_EMAIL_LIMIT ||
        errorCode === DISABLE_CERTIFICATION_BY_PHONENUMBER_LIMIT ||
        errorCode === DISABLE_CERTIFICATION_BY_REMOTE_ADDR_LIMIT ||
        errorCode === DISABLE_CREATE_CERTIFICATION_BY_EMAIL_LIMIT
      ) {
        preorderCommonStore().errorMessage.bannerCoupon.disableCertificationButton = true;
        preorderCommonStore().stopTimer(preorderCommonStore().getTimer);
        _showErrorAndFocus(false);
      } else {
        _showErrorAndFocus();
      }
    },

    /**
     * keyup enter 이슈
     * 한글 v-model 이슈
     * https://github.com/vuejs/vue/issues/10277
     * https://minjung-jeon.github.io/IME-keyCode-229-issue/
     */

    // 쿠폰 조회 클릭시 실행할 함수
    onClickFindCoupon() {
      if (!preorderCommonStore().validateEmailOrPhoneAndCertification('bannerCoupon')) return;
      this.callFindCoupon({
        gameName: preorderCommonStore().parentData.gameName,
        phoneNumber: this.getInputedPhone,
        email: this.userInputedData.inputedEmail,
        country: this.getInputedCountry,
        certificationCode: this.userInputedData.inputedCertifyNum,
      });
    },

    // 인증번호 발송
    async onClickCertificationButton() {
      if (
        preorderCommonStore().getUsePhone &&
        !preorderCommonStore().validatePhoneNumber('bannerCoupon')
      )
        return;
      if (preorderCommonStore().getUseEmail && !preorderCommonStore().validateEmail('bannerCoupon'))
        return;
      await this.callCertifyNumber({
        // 인증번호 전송 API를 위한 body payload
        gameName: preorderCommonStore().parentData.gameName,
        country: this.getInputedCountry,
        phoneNumber: this.getInputedPhone,
        email: this.userInputedData.inputedEmail,
        extraData: getExtraData(),
      });
      setTimeout(() => this.bannerCertifyInput?.focus());
    },

    // 운영툴에서만 쓰는 함수
    onClickFindCouponForAdmin() {
      if (!preorderCommonStore().validateEmailOrPhoneAndCertification('bannerCoupon')) return;
      this.bannerCouponStep++;
      setTimeout(() => this.bannerCertifyInput?.focus());
    },
    setMockCouponData() {
      this.userData = maskingUserInfo(
        '01012345678',
        '',
        preorderCommonStore().getLocale === 'zh_TW'
      );
      this.couponList = formatCouponList(
        'couponNo=OOOOOOOOOOOOOO:혈석 2000개^OOOOOOOOOOOOOO:도화의 신석 패키지,'
      );
    },
    createEmailorPhoneCertificationForFindInAdmin() {
      if (
        preorderCommonStore().getUsePhone &&
        !preorderCommonStore().validatePhoneNumber('bannerCoupon')
      )
        return;
      if (preorderCommonStore().getUseEmail && !preorderCommonStore().validateEmail('bannerCoupon'))
        return;
      preorderCommonStore().resetErrorMessage();
      this.userInputedData.inputedCertifyNum = '';
      this.isInResendProcess = true; // 한번이라도 인증 번호 발송했다면 이후부터는 재발송 절차
      this.isSendedCertifyNum = true;
      preorderCommonStore().startTimer('bannerCoupon');
      setTimeout(() => this.bannerCertifyInput?.focus());
    },
  },
});
